/**
 * Contrat de domaine exiPlan — partagé serveur ↔ client.
 *
 * Ce module ne contient **aucune** dépendance : uniquement des constantes gelées et des
 * typedefs JSDoc. Il fait foi pour les valeurs d'énumération persistées en base ;
 * toute valeur absente d'ici est un bug (validation Zod côté serveur, sélecteurs côté client).
 */

/* ------------------------------------------------------------------ identité */

/** Fournisseurs d'identité acceptés. `local` = bypass de développement uniquement. */
export const AUTH_PROVIDERS = Object.freeze(['microsoft', 'github', 'google', 'local']);

/**
 * Langues de l'interface. `users.locale` ne peut valoir que l'une d'elles.
 *
 * ⚠️ Cette liste doit rester alignée sur `supportedLngs` d'`i18n/config.js` : la valeur revient du
 * serveur et est réinjectée dans `i18n.changeLanguage()` à l'ouverture de session. Une langue
 * acceptée ici mais absente des ressources afficherait des clés brutes.
 */
export const LOCALES = Object.freeze(['fr', 'en']);

/** Rôles au sein d'une équipe, du plus fort au plus faible. L'ordre porte la hiérarchie. */
export const TEAM_ROLES = Object.freeze(['owner', 'admin', 'member', 'viewer']);

/** Rang d'un rôle : plus petit = plus de droits. */
export const ROLE_RANK = Object.freeze(
  Object.fromEntries(TEAM_ROLES.map((role, index) => [role, index])),
);

/**
 * Indique si `role` détient au moins les droits de `required`.
 * @param {string|null|undefined} role
 * @param {string} required
 * @returns {boolean}
 */
export function roleAtLeast(role, required) {
  if (!role || !(role in ROLE_RANK) || !(required in ROLE_RANK)) return false;
  return ROLE_RANK[role] <= ROLE_RANK[required];
}

/* --------------------------------------------------------------- organisation */

/**
 * Rôles au sein d'une organisation — nomenclature D3 (ROL-2), codes stockés tels quels, du plus
 * fort au plus faible : `proprietaire`, `administrateur`, `facturation`, `editeur`, `lecteur`.
 *
 * ⚠️ `facturation` n'est **pas** un cran d'accès aux projets : c'est un rôle d'administration
 * financière. Il est classé sous `administrateur` pour l'ordre d'affichage, si bien que le droit
 * de modifier le quota de sièges (`proprietaire` ou `facturation` en mode `local`) n'est pas
 * exprimable par le rang seul — il s'écrit explicitement dans `organizations.service.js`.
 * Reclasser `facturation` au-dessus d'`administrateur` lui donnerait des droits d'administration
 * qu'il ne doit pas avoir.
 */
export const ORG_ROLES = Object.freeze([
  'proprietaire',
  'administrateur',
  'facturation',
  'editeur',
  'lecteur',
]);

/** Rôles qu'une invitation d'organisation peut accorder (ACC-4 : jamais `proprietaire`). */
export const ORG_ROLES_INVITABLES = Object.freeze([
  'administrateur',
  'facturation',
  'editeur',
  'lecteur',
]);

export const ORG_ROLE_RANK = Object.freeze(
  Object.fromEntries(ORG_ROLES.map((role, index) => [role, index])),
);

/**
 * Indique si `role` détient au moins les droits d'organisation de `required`.
 * @param {string|null|undefined} role
 * @param {string} required
 * @returns {boolean}
 */
export function orgRoleAtLeast(role, required) {
  if (!role || !(role in ORG_ROLE_RANK) || !(required in ORG_ROLE_RANK)) return false;
  return ORG_ROLE_RANK[role] <= ORG_ROLE_RANK[required];
}

/**
 * Nature d'une organisation (décision studio ORG-1 à ORG-8, 2026-09-28).
 * `personnel` : l'espace solo de chaque compte, jamais présenté comme une organisation.
 * `organisation` : l'espace collaboratif, facturé — au plus une par compte (ORG-4).
 */
export const ORGANIZATION_KINDS = Object.freeze(['personnel', 'organisation']);

/**
 * Statut d'une adhésion à une organisation.
 * Seul `active` consomme un siège. `requested` est une demande spontanée de rattachement
 * par domaine e-mail : aucun droit tant qu'un admin ne l'a pas approuvée.
 */
export const MEMBERSHIP_STATUSES = Object.freeze(['active', 'requested', 'suspended']);

/**
 * Codes d'erreur (`details.code`) du lot O3 « organisation studio » et statut HTTP associé.
 * Documentaires : les routes lèvent `ApiError(statut, message, { code })`, le client lit `code`.
 */
export const ORG_STUDIO_ERROR_CODES = Object.freeze({
  SEAT_REQUIRED: 403,
  MSAL_REQUIS: 403,
  REGISTRE_INDISPONIBLE: 503,
  REGISTRE_OCCUPE: 503,
  SIEGE_SUSPENDU: 409,
  ORG4_ADHESION_EXISTANTE: 409,
  PLAFOND_CREATION: 409,
  ARBITRAGE_REQUIS: 409,
  // Tranche T5 (parcours registre) : refus du registre relayés et refus propres au produit.
  ROLE_INSUFFISANT: 403,
  DERNIER_PROPRIETAIRE: 409,
  SEAT_LIMIT_REACHED: 409,
  ETAT_MODIFIE: 409,
  /** Retrait, suspension ou réactivation de son propre siège. */
  SIEGE_SOI_MEME: 409,
  /** Réactivation manuelle d'un siège suspendu par la grâce LIC-8. */
  SIEGE_SUSPENDU_GRACE: 409,
  /** Un `facturation` s'attribue ou se réactive un siège à lui-même. */
  FACTURATION_SANS_SIEGE: 403,
  /** Réactivation de son propre siège par un rôle autre que `proprietaire`. */
  REACTIVATION_PROPRE_REFUSEE: 403,
  /** « Demander un siège » depuis une clé API ou un agent (ACC-5). */
  CANAL_REFUSE: 403,
  /** Invitation d'équipe ou de projet acceptée sans être membre actif de l'organisation. */
  ADHESION_REQUISE: 403,
});

/** Portée d'une invitation : à quoi l'invité est-il convié ? */
export const INVITATION_SCOPES = Object.freeze(['organization', 'team', 'project']);

/**
 * Domaines grand public : ne peuvent jamais être revendiqués par une organisation.
 * Sans cette liste, la première société qui déclare `gmail.com` capte tous les comptes Gmail.
 */
export const PUBLIC_EMAIL_DOMAINS = Object.freeze([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'outlook.fr',
  'hotmail.com',
  'hotmail.fr',
  'live.com',
  'live.fr',
  'msn.com',
  'yahoo.com',
  'yahoo.fr',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'gmx.fr',
  'free.fr',
  'orange.fr',
  'wanadoo.fr',
  'sfr.fr',
  'laposte.net',
  'bbox.fr',
  'numericable.fr',
  'mailinator.com',
  'yopmail.com',
  'tempmail.com',
]);

/**
 * @param {string|null|undefined} domain
 * @returns {boolean}
 */
export function isPublicEmailDomain(domain) {
  if (!domain || typeof domain !== 'string') return false;
  return PUBLIC_EMAIL_DOMAINS.includes(domain.trim().toLowerCase());
}

/* ------------------------------------------------------------------- projets */

export const PROJECT_STATUSES = Object.freeze(['draft', 'active', 'on_hold', 'done', 'archived']);

/** Portées d'un lien de partage public. `edit` n'est volontairement pas proposé. */
export const SHARE_SCOPES = Object.freeze(['read', 'comment']);

/* ------------------------------------------------------------ clés API (MCP) */

/**
 * Portées d'une clé API — le jeu est **gelé** et fait foi pour les deux côtés.
 *
 * Une clé API ne donne jamais plus que ce que son porteur possède déjà : la portée
 * **restreint**, elle n'élève pas. Un `tasks:write` porté par une clé dont le propriétaire est
 * `viewer` sur le projet ne permet toujours rien — `resolveAccess()` tranche en dernier.
 *
 * L'écriture est isolée dans sa propre portée : la valeur d'un agent est d'abord de lire, et
 * une clé de lecture seule doit rester le cas par défaut.
 */
export const API_KEY_SCOPES = Object.freeze([
  'projects:read',
  'tasks:read',
  'tasks:write',
  'stats:read',
]);

/** Préfixe des jetons de clé API. Reconnaissable dans un journal ou un dépôt fuité. */
export const API_KEY_PREFIX = 'exip_ak_';

/**
 * Canal d'origine d'une ligne du journal d'activité (standard MCP § 6, MCP-T1) :
 * `ui` (session navigateur), `mcp` (jeton OBO échangé par le serveur MCP hébergé pour le
 * compte d'un utilisateur), `agent` (clé API — agent autonome, § MCP-K1..K6).
 */
export const ACTIVITY_CANAUX = Object.freeze(['ui', 'mcp', 'agent']);

/**
 * Vrai si `scopes` couvre `required`.
 * @param {readonly string[]} scopes
 * @param {string} required
 */
export function hasScope(scopes, required) {
  return Array.isArray(scopes) && scopes.includes(required);
}
/* -------------------------------------------------------------------- tâches */

/**
 * Statut « logique » d'une tâche, indépendant de la colonne kanban.
 * La colonne porte le flux de travail du projet ; le statut porte la sémantique
 * transverse (utilisée par le Gantt, les stats et les exports).
 */
export const TASK_STATUSES = Object.freeze(['todo', 'in_progress', 'blocked', 'review', 'done']);

/** Statuts considérés comme terminés (avancement = 100 %). */
export const DONE_STATUSES = Object.freeze(['done']);

/** Priorités, de la plus faible à la plus forte. */
export const TASK_PRIORITIES = Object.freeze(['low', 'medium', 'high', 'critical']);

/**
 * Types de dépendance entre deux tâches (norme planification).
 * FS = fin→début, SS = début→début, FF = fin→fin, SF = début→fin.
 */
export const DEPENDENCY_TYPES = Object.freeze(['FS', 'SS', 'FF', 'SF']);

/** Types d'entrée du Gantt : une tâche ordinaire, un jalon (date unique), un lot (parent). */
export const TASK_KINDS = Object.freeze(['task', 'milestone', 'phase']);

/* -------------------------------------------------------------------- charge */

/**
 * Charge de travail (vue d'ensemble). Définitions : docs/architecture/vue-ensemble.md §1.
 * Réglables en un seul endroit — le serveur calcule, le client affiche et n'a que les
 * libellés à connaître.
 */
export const WORKLOAD = Object.freeze({
  /** Capacité hebdomadaire d'un membre, en heures. Un réglage par membre est hors périmètre. */
  weeklyCapacityHours: 35,
  /** Taux à partir duquel un membre est « chargé » (inclus). */
  loadedRatio: 0.8,
  /** Taux au-delà duquel un membre est en « surcharge » (strict). */
  overloadedRatio: 1.0,
});

/** Niveaux de charge, du plus calme au plus tendu. `WORKLOAD_LEVELS[i]` porte l'ordre. */
export const WORKLOAD_LEVELS = Object.freeze(['available', 'loaded', 'overloaded']);

/**
 * Niveau de charge d'un taux (charge ÷ capacité).
 * @param {number} ratio
 * @returns {'available'|'loaded'|'overloaded'}
 */
export function workloadLevel(ratio) {
  if (ratio > WORKLOAD.overloadedRatio) return 'overloaded';
  if (ratio >= WORKLOAD.loadedRatio) return 'loaded';
  return 'available';
}

/* -------------------------------------------------------------- indicateurs */

/** Seuils de la météo d'un projet. Définitions : docs/architecture/vue-ensemble.md §2. */
export const HEALTH = Object.freeze({
  /** Écart de planning (points) sous lequel la météo est rouge. */
  redScheduleGap: -15,
  /** Écart de planning (points) sous lequel la météo est orange. */
  amberScheduleGap: -5,
  /** Part de tâches ouvertes en retard au-delà de laquelle la météo est rouge. */
  redOverdueRatio: 0.2,
  /** Nombre de semaines de la série de débit. */
  throughputWeeks: 8,
});

/** Météo d'un projet. `unknown` = aucune donnée exploitable, jamais assimilé à « mauvais ». */
export const HEALTH_WEATHERS = Object.freeze(['green', 'amber', 'red', 'unknown']);

/* ------------------------------------------------------------------ activité */

/** Verbes du journal d'activité — un verbe absent d'ici ne doit pas être écrit en base. */
export const ACTIVITY_VERBS = Object.freeze([
  'project.created',
  'project.updated',
  'project.archived',
  'project.transferred',
  // Suppression définitive d'un projet (réglages du projet) — trace rattachée à l'organisation,
  // jamais au projet : un journal rattaché au projet serait effacé avec lui.
  'project.deleted',
  'team.deleted',
  'task.created',
  'task.updated',
  'task.moved',
  'task.deleted',
  'task.assigned',
  'comment.created',
  'member.added',
  'member.removed',
  'member.role_changed',
  'share.created',
  'share.revoked',
  'import.csv',
  'export.csv',
  'export.pdf',
  'org.created',
  'org.updated',
  'org.domain_claimed',
  'org.domain_verified',
  'org.autojoin_changed',
  'org.seats_changed',
  'member.invited',
  'member.invitation_revoked',
  'member.joined',
  'member.requested',
  'member.request_approved',
  'member.request_rejected',
  // Retrait d'une demande par son auteur (I2, revue finale du modèle d'organisation).
  'member.request_withdrawn',
  'member.suspended',
  'member.reactivated',
  'project_member.added',
  'project_member.updated',
  'project_member.removed',
  'org.deleted',
  // Historique : consentement posé localement avant O8 (désormais porté par le registre). Les
  // lignes d'`activity_logs` existantes gardent ce verbe.
  'organization.marketing_consent',
  // Standard MCP § 6 (MCP-T2) : audit des refus et erreurs sur la surface d'agent
  // (`/api/mcp/*`) — colonne `verb` posée en NVARCHAR nu côté MSSQL (§ migration 001, aucune
  // contrainte CHECK), ces deux valeurs n'exigent donc aucune migration de schéma.
  'mcp.access_refused',
  'mcp.write_failed',
]);

/* --------------------------------------------------------------- préférences */

/*
 * Préférences utilisateur — une énumération par réglage, et **une colonne typée par
 * préférence** en base, jamais un JSON fourre-tout. Deux raisons, dont une propre à ce
 * dépôt : le schéma reste lisible et contraignable (CHECK, défaut, NOT NULL), et surtout
 * SQLite (dév) et MSSQL (prod) ne stockent pas le JSON de la même façon — une préférence
 * en JSON se comporterait différemment selon le dialecte. Le prix assumé : chaque nouvelle
 * préférence coûte une migration. C'est le bon prix.
 */

/**
 * Largeur du corps de page. `pleine` = aucune borne.
 *
 * Les trois valeurs numériques sont des **chaînes**, pas des nombres : elles sont
 * persistées telles quelles et un `1280` numérique doit être **refusé**, pas converti
 * silencieusement — sans quoi la validation laisserait entrer deux représentations de la
 * même valeur. Même énumération que le token `--largeur-corps` d'exiWatch, pour que les
 * produits du studio convergent au lieu de chacun inventer sa grille.
 */
export const PAGE_WIDTHS = Object.freeze(['pleine', '1440', '1280', '1024']);

/**
 * Valeur CSS à poser dans `--largeur-corps` pour une largeur donnée.
 *
 * `none` et non `100%` : la variable alimente un `max-width`, et `100%` bornerait au parent,
 * ce qui casserait tout conteneur à débordement horizontal (le Gantt, notamment).
 * @param {string|null|undefined} width
 * @returns {string}
 */
export function pageWidthToCss(width) {
  return !width || width === 'pleine' ? 'none' : `${width}px`;
}

/**
 * Thème d'affichage. `system` suit le réglage du système d'exploitation.
 *
 * ⚠️ Conséquence côté client : `system` ne s'écrit **pas** dans le miroir `localStorage`,
 * il en **retire** la clé — l'absence de clé vaut `system`. C'est ce que lit le script
 * anti-flash avant peinture ; y écrire la chaîne `'system'` le casserait.
 */
export const THEME_MODES = Object.freeze(['system', 'light', 'dark']);

/** Regroupement des notifications. `instant` = une notification par événement. */
export const NOTIFY_DIGESTS = Object.freeze(['instant', 'daily', 'weekly']);

/**
 * Familles d'événements notifiables — la granularité du réglage.
 *
 * La granularité est **par famille d'événement, globale**, et délibérément pas une matrice
 * projet × événement : l'étude du marché (2026-09-15) montre que le réglage par projet
 * n'est pas le standard — ni Linear, ni ClickUp, ni Notion ne l'ont. Un désabonnement par
 * élément viendra si le besoin se confirme ; il ne se déduit pas d'ici.
 *
 * Ces familles agrègent les verbes d'`ACTIVITY_VERBS` : elles ne les remplaçent pas et
 * n'ont pas à être en correspondance un pour un.
 */
export const NOTIFY_EVENTS = Object.freeze([
  'task.assigned',
  'task.due',
  'comment.mention',
  'comment.reply',
  'project.shared',
  'org.membership',
]);

/**
 * Motif d'une heure au format `HH:MM` sur 24 h, pour les heures calmes.
 *
 * Une **chaîne** et non des minutes depuis minuit : c'est ce que rend un `<input type="time">`,
 * et c'est lisible en base sans conversion. La plage peut enjamber minuit (`22:00` → `07:00`) —
 * `début > fin` est donc valide, ce n'est pas une erreur de saisie à rejeter.
 */
export const TIME_OF_DAY_PATTERN = '^([01][0-9]|2[0-3]):([0-5][0-9])$';

/** Valeurs par défaut des préférences — miroir exact des défauts de la colonne en base. */
export const PREFERENCE_DEFAULTS = Object.freeze({
  pageWidth: 'pleine',
  theme: 'system',
  notifyEmail: true,
  notifyInApp: true,
  notifyDigest: 'instant',
  notifyTaskAssigned: true,
  notifyTaskDue: true,
  notifyCommentMention: true,
  notifyCommentReply: true,
  notifyProjectShared: true,
  notifyOrgMembership: true,
  quietHoursEnabled: false,
  quietHoursStart: '19:00',
  quietHoursEnd: '08:00',
});

/* ------------------------------------------------------------------- limites */

export const LIMITS = Object.freeze({
  /** Pagination : valeur par défaut et plafond dur côté serveur. */
  pageSize: 50,
  maxPageSize: 200,
  /** Nombre maximal de lignes acceptées dans un import CSV. */
  maxImportRows: 5000,
  /**
   * Nombre maximal de dépendances entrantes sur une même tâche.
   *
   * Plafond de **métier autant que de sûreté** : au-delà, le graphe n'est plus lisible par un
   * humain, et la détection de cycle coûte un parcours par dépendance. Sans lui, une seule
   * requête d'import pouvait bloquer l'event loop plus d'une minute (issue #47).
   *
   * Lu par les DEUX surfaces — `addDependency` (API REST) et l'import CSV. N'en faire respecter
   * qu'une laisserait l'autre ouverte : c'est exactement ce que l'audit a trouvé.
   */
  maxDependenciesPerTask: 50,
  /**
   * Budget de parcours de graphe pour la validation d'un import, en nombre de nœuds visités.
   *
   * La détection de cycle coûte `O(jetons × (V+E))` : plafonner les dépendances par ligne ne
   * suffit pas, puisque le produit reste explosif sur un fichier large doté d'un moyeu à fort
   * éventail. Ce budget est la seule borne qui tienne sans redessiner la détection de cycle.
   * Un fichier qui le dépasse est **refusé avec un message clair**, jamais subi en silence.
   *
   * La valeur laisse passer très largement un import légitime (5 000 lignes, quelques
   * dépendances chacune, graphes peu profonds).
   */
  maxImportGraphVisits: 2_000_000,
  /**
   * Profondeur maximale de l'arbre des tâches, racine comprise.
   *
   * Sans plafond, un arbre pathologique rend le Gantt illisible et fait croître le coût des
   * remontées sans borne.
   */
  maxTaskDepth: 5,
  /** Longueurs maximales, alignées sur les colonnes Sequelize. */
  titleLength: 240,
  nameLength: 120,
  descriptionLength: 10_000,
  /**
   * Nombre de sièges accordés à la création d'une organisation.
   *
   * C'est le **plan de base**, et il n'est pas modifiable par le client : le nombre de licences
   * relève de ce que l'organisation a souscrit auprès d'exiPlan, pas d'un réglage qu'un
   * administrateur s'accorde à lui-même. L'interface expose l'usage et un bouton « augmenter le
   * plan » inactif ; le relèvement se fera depuis le portail d'administration exi* (phase 2).
   */
  seatDefault: 3,
  /** Plafond dur du quota de sièges, garde-fou du service d'administration. */
  maxSeatLimit: 10_000,
  /** Durée de validité d'une invitation, en jours. */
  invitationTtlDays: 14,
  /**
   * Fraîcheur de session exigée avant un geste irréversible (suppression d'organisation).
   *
   * Au-delà, `DELETE /organizations/:id` refuse avec `401 { code: 'REAUTH_REQUIRED' }` en mode
   * `session` — ni en mode `bypass` (dev), ni en mode `api-key` (la surface d'agent n'atteint de
   * toute façon jamais cette route). Le client redirige alors vers
   * `/api/auth/:provider/start?returnTo=…` pour rafraîchir la session.
   */
  reauthMaxAgeMinutes: 15,
  /**
   * Fenêtre maximale d'une requête de la vue d'ensemble, en jours (bornes incluses).
   * 92 = un trimestre : au-delà, la grille n'est plus lisible et la requête charge trop.
   */
  portfolioMaxDays: 92,
  /**
   * Plafond de tâches renvoyées ou agrégées par une route `/portfolio/*`. Au-delà, la réponse
   * porte `truncated: true` et s'arrête — jamais une erreur, jamais un silence.
   */
  portfolioMaxTasks: 5000,
});

/* -------------------------------------------------------------------- couleur */

/**
 * Accent produit exiPlan — orchidée, palette pastel du studio (@exidis/ui 1.7.0, 2026-09-28).
 * Source de vérité : `@exidis/ui/brand` (`PRODUITS.exiPlan`) et docs/design/accent-exiplan.md.
 * Répété ici pour les rendus non-CSS (PDF serveur, SVG exporté), en thème CLAIR : le pastel
 * (`accent`) ne remplit que des surfaces ; tout filet, barre fine ou texte coloré prend
 * `accentTrait` / `accentInk` (l'encre — l'orchidée sur blanc ne mesure que 1,92:1).
 */
export const BRAND = Object.freeze({
  accent: '#e3a6ec',
  accentInk: '#8a1d96',
  accentWash: '#fbedfd',
  accentTrait: '#8a1d96',
  ink: '#131316',
  inkSoft: '#52525b',
  line: '#ececef',
});

/* ------------------------------------------------------------------ typedefs */

/**
 * @typedef {object} User
 * @property {string} id
 * @property {string} email
 * @property {string} name
 * @property {'microsoft'|'github'|'google'|'local'} provider
 * @property {string|null} avatarUrl
 * @property {string|null} jobTitle
 */

/**
 * @typedef {object} Organization
 * @property {string} id
 * @property {string} name
 * @property {string|null} primaryDomain         Domaine e-mail revendiqué, jamais un domaine grand public.
 * @property {string|null} domainVerifiedAt      ISO 8601, `null` tant que le domaine n'est pas vérifié.
 * @property {boolean} autoJoinVerifiedDomain    Autorise la demande spontanée de rattachement par domaine.
 * @property {number} seatLimit
 */

/**
 * @typedef {object} OrgMember
 * @property {string} id
 * @property {string} organizationId
 * @property {string} userId
 * @property {'proprietaire'|'administrateur'|'facturation'|'editeur'|'lecteur'} role
 * @property {'active'|'requested'|'suspended'} status
 */

/**
 * @typedef {object} Invitation
 * @property {string} id
 * @property {string} organizationId
 * @property {'organization'|'team'|'project'} scopeType
 * @property {string|null} scopeId    `null` si `scopeType === 'organization'`.
 * @property {string} email
 * @property {string} tokenHash       Empreinte SHA-256 du jeton. Le jeton en clair n'est
 *   **jamais** persisté — il n'existe qu'au moment de la création (§10 de la spec) et n'est donc
 *   volontairement pas un champ de ce typedef.
 * @property {string} expiresAt       ISO 8601.
 * @property {string|null} revokedAt
 */

/**
 * @typedef {object} ProjectMember
 * @property {string} id
 * @property {string} projectId
 * @property {string} userId
 * @property {'admin'|'member'|'viewer'} role   Surcharge du rôle d'équipe sur ce projet — `owner`
 *   est volontairement absent : la propriété est une notion d'équipe, pas de projet (cf.
 *   `server/src/validation/schemas.js::projectMemberRoleSchema` et
 *   `project-members.service.js`).
 * @property {boolean} canDelete
 */

/**
 * @typedef {object} Project
 * @property {string} id
 * @property {string} teamId
 * @property {string} key      Trigramme/quadrigramme unique par équipe (ex. `APOL`).
 * @property {string} name
 * @property {string|null} description
 * @property {'draft'|'active'|'on_hold'|'done'|'archived'} status
 * @property {string|null} startDate  ISO `YYYY-MM-DD`.
 * @property {string|null} endDate    ISO `YYYY-MM-DD`.
 */

/**
 * @typedef {object} Task
 * @property {string} id
 * @property {string} projectId
 * @property {string|null} columnId
 * @property {string|null} parentId
 * @property {number} number    Numéro séquentiel par projet ; la référence lisible est `KEY-number`.
 * @property {string} title
 * @property {string|null} description
 * @property {'task'|'milestone'|'phase'} kind
 * @property {'todo'|'in_progress'|'blocked'|'review'|'done'} status
 * @property {'low'|'medium'|'high'|'critical'} priority
 * @property {string|null} assigneeId
 * @property {string|null} startDate
 * @property {string|null} dueDate
 * @property {number} progress  0-100.
 * @property {number|null} estimateHours
 * @property {number} position  Ordre dans la colonne kanban.
 */

/**
 * @typedef {object} Dependency
 * @property {string} id
 * @property {string} predecessorId
 * @property {string} successorId
 * @property {'FS'|'SS'|'FF'|'SF'} type
 * @property {number} lagDays  Décalage en jours, négatif autorisé (chevauchement).
 */
